<script>
  import { onMount } from 'svelte';
  import TitleCard from '../assets/general/titlecard.svelte';
  import TitleAndText from '../assets/general/titleandtext.svelte';
  import Button from '../routes/button.svelte';
  import Img from '../assets/general/ImageLightBox.svelte';
  import Video from '../assets/home/video.svelte';

  onMount(() => {
    // Add a 250ms delay
    setTimeout(() => {
      console.log("Component mounted. Processing URL parameters after 500ms delay...");

      // Get the URLSearchParams object from the current window location
      const params = new URLSearchParams(window.location.search);
      console.log("URL parameters:", params.toString());

      // Extract the 'section' parameter from the URL
      const sectionId = params.get('section');
      console.log("Section ID from URL parameter:", sectionId);

      if (sectionId) {
        // Find the element with the corresponding ID
        const sectionElement = document.getElementById(sectionId);
        console.log("Element found for section ID:", sectionElement);

        if (sectionElement) {
          // Scroll the element into view
          sectionElement.scrollIntoView({ behavior: 'smooth' });
          console.log("Scrolled into view:", sectionId);
        } else {
          console.log("No element found with ID:", sectionId);
        }
      } else {
        console.log("No 'section' parameter found in URL.");
      }
    }, 250); // Delay set to 250 milliseconds
  });
</script>

<main>
	<TitleCard
	header="The Foundation"
	backgroundImage="OldOffice.jpg"
	/>
	<TitleAndText
		title="A book. A movie. A movement."
		text="For 20 years, the Freedom Writers Foundation has mitigated student dropout rates by reproducing the Freedom Writers’ experience in schools around the world. <strong><em>The Foundation’s mission is to provide educators with tools to empower all students to succeed.</em></strong> Our core programs include Training, Outreach, Scholarship, and Curriculum.<br><br>The Training Program includes a 5-day intensive workshop led by Erin Gruwell that helps educators of all subjects and grade levels focus on the humanity of every student. Through keynote speeches by Erin and the Original Freedom Writers, the Outreach Program improves classroom attendance, increases student and employee engagement, and fosters inclusive campus atmospheres. The Scholarship Program provides support for first-generation college students who have remarkable academic promise despite tremendous adversity.<br><br>The Foundation’s growing library of curriculum includes all of the Freedom Writer books, a Teacher’s Guide, a podcast with companion curriculum, and other digital media. To stay connected with Erin and the Freedom Writers, follow the Freedom Writers Foundation on social media and share your stories with <a href='mailto:stories@freedomwritersfoundation.org'>stories@freedomwritersfoundation.org</a>."
	/>
	<div class="button-container">
		<Button
			text="OUR PROGRAMS"
			link="../outreach"
		/>
	</div>
	<div class="video-container">
		<Video
			videoSRC="https://www.youtube.com/embed/D6HFo7b28ko?si=BJTFku8LdnN01yV6"
		/>
	</div>
<section id="OurStory">
	<TitleCard
	header="Our Story"
	backgroundImage="FWDCoverBW.jpg"
	/>
	<TitleAndText
		title="From <q>at-risk</q> to authors."
		text="In 1994, Long Beach was a racially divided community filled with drugs, gang warfare, and homicides, and the tensions on the streets had carried into the school halls. When idealistic first-year teacher Erin Gruwell walked into Room 203 at Wilson High School, her students had already been labeled <q>unteachable.</q> But Gruwell believed in something more.<br><br>Despite opposition from fellow teachers, Ms. G sought to engage her students using relevant literature and media. Her students were particularly inspired by the writings of Anne Frank and Zlata Filipovic, and ultimately chose to put down their weapons and pick up a pen. The once-hardened teens discovered a new way to express themselves and embrace history, humanity, and hope. By sharing their stories, they rewrote their futures and became catalysts for change."
	/>
	<div class="image-container">
		<Img
			imageUrl="../images/other/EGAndFreedomWriterBW.jpg"
			imageWidth="434px"
			imageHeight="auto"
			title="Erin Gruwell and Freedom Writers"
			altText="A black and white photo of Erin Gruwell and several Freedom Writers."
		/>
		<Img
			imageUrl="../images/other/YearbookBW.jpg"
			imageWidth="434px"
			imageHeight="auto"
			title="Yearbook Photos of Freedom Writers"
			altText="A black and white collage of several Freedom Writers' yearbook photos."
		/>
		<p>All 150 of the Freedom Writers went on to graduate in 1998 and their stories were published in a book called <em>The Freedom Writers Diary</em>, which became a <em>New York Times</em> Bestseller and was adapted into the feature film <em>Freedom Writers</em> starring Hilary Swank. Many of Gruwell’s students pursued higher education and now have successful careers. They created the Freedom Writers Foundation to help educators reproduce the success of Room 203 in classrooms around the world.<br><br><strong>Support the Freedom Writers legacy and help teachers empower their students every day.</strong></p>
	</div>
	<div class="button-container">
		<Button
			text="DONATE HERE"
			link="../donate"
		/>
	</div>
</section>
<section id="ErinGruwell">
	<TitleCard
	header="Erin Gruwell"
	backgroundImage="ErinGruwellPresenting.jpg"
	/>
	<TitleAndText
		title="A teacher of hope."
		text="Erin Gruwell has earned an award-winning reputation for her steadfast commitment to the future of education. Her impact as a teacher attracted Hollywood’s attention, and in 2007, Paramount Pictures released <em>Freedom Writers</em>, starring two-time Academy Award winner Hilary Swank as Erin. The film is based on <em>The Freedom Writers Diary</em>, the <em>New York Times</em> Bestseller that chronicles Erin’s extraordinary journey with her 150 high school students who dubbed themselves the <q>Freedom Writers</q> in homage to the Freedom Riders of the Civil Rights Movement.<br><br>By fostering an educational philosophy that values and promotes diversity, Erin transformed her students’ lives. She encouraged them to re-think rigid beliefs about themselves and others, reconsider their own daily decisions, and ultimately, re-chart their future. With Erin’s support, they chose to forego teenage pregnancy, drugs, and violence in order to become aspiring college students, published authors, and catalysts for change."
	/>
	<div class="image-text-container">
		<div class="text-wrapper">
		<p>Erin founded the Freedom Writers Foundation where she currently teaches educators around the world how to implement her innovative lesson plans into their own classrooms. Erin’s teaching methods from her time in Room 203 have been published in her powerful call to arms <em>Teach With Your Heart</em>, as well as <em>The Freedom Writers Diary Teacher’s Guide</em>. Erin and teachers she has trained known as <q>Freedom Writer Teachers</q> collaborated to write <em>Teaching Hope</em>, a book in which stories <q>from the front lines of education take [the reader] from the anticipation of the first day to the disillusionment, challenges, and triumphs of the school year.</q><br><br>Erin and her students have appeared on numerous television shows, including <em>Oprah</em>, <em>Prime Time Live</em> with Connie Chung, Barbara Walters’ <em>The View</em>, and <em>Good Morning America</em>. Their story has earned them dozens of awards, most notably the Spirit of Anne Frank Award. Erin Gruwell and the Freedom Writer Teachers also collaborated with Scholastic to create <em>On the Record</em>, an innovative reading and writing program. <em>On the Record</em> uses the Freedom Writers Methodology to improve students’ reading and writing skills, as well as the quality of their lives.</p>
		</div>
		<div class="image-container">
		<Img
			imageUrl="../images/other/EGSigning.jpg"
			imageWidth="auto"
			imageHeight="350px"
			title="Erin Gruwell signing a book."
			altText="A photo of Erin Gruwell signing a book."
		/>
		</div>
	</div>
	<div class="below-text">
		<p>All 150 of the Freedom Writers went on to graduate in 1998 and their stories were published in a book called <em>The Freedom Writers Diary</em>, which became a <em>New York Times</em> Bestseller and was adapted into the feature film <em>Freedom Writers</em> starring Hilary Swank. Many of Gruwell’s students pursued higher education and now have successful careers. They created the Freedom Writers Foundation to help educators reproduce the success of Room 203 in classrooms around the world.<br><br><strong>Support the Freedom Writers legacy and help teachers empower their students every day.</strong></p>
	<div class="button-container">
		<Button
			text="REQUEST ERIN GRUWELL"
			link="mailto:rick@freedomwritersfoundation.org"
		/>
	</div>
	</div>
	<div class="video-container">
		<Video
			videoSRC="https://www.youtube.com/embed/UFKMo9Vt4tQ?si=M8hecaORvCI3Keb9"
		/>
	</div>
</section>
<section id="FreedomWriters">
	<TitleCard
	header="Freedom Writers"
	backgroundImage="FreedomWritersGroupShot.jpg"
	/>
	<TitleAndText
		title="Speakers and activists."
		text="On their first day of high school, Erin Gruwell’s students had three things in common: they hated school, they hated each other, and they hated her. But all of that changed when they discovered the power of telling their stories. Against all odds, all 150 of them went on to graduate, became published authors, and started a world-wide movement to change the education system as we know it.<br><br>In the years that followed, many of them attended college, began successful careers, and established families of their own. Now, more than 20 years later, they are changing the world as teachers, volunteers, writers, architects, nurses, and techies.<br><br>The Freedom Writers and Ms. G remain close to this day. In fact, many of the Freedom Writers work with the Freedom Writers Foundation, speaking to schools and organizations through the Outreach Program and training teachers from all over the world.<br><br>They speak about topics such as homelessness, addiction recovery, bullying, depression and suicide awareness, and how to use storytelling to overcome adversity. You can invite a Freedom Writer to present to your organization through a Speaking Engagement or a Video Chat. They are also featured with Erin Gruwell on the <em>Freedom Writers Podcast</em>, found wherever you download podcasts."
	/>
	<div class="image-container">
		<Img
			imageUrl="../images/other/TyeAndSueEllen.png"
			imageWidth="434px"
			imageHeight="auto"
			title="Tye and Sue Ellen visit students"
			altText="Original Freedom Writers Tye and Sue Ellen visit a classroom and visit with students."
		/>
		<Img
			imageUrl="../images/other/TonyAndNarada.jpg"
			imageWidth="434px"
			imageHeight="auto"
			title="Tony and Narada Presenting"
			altText="Original Freedom Writers Tony and Narada present onstage to students."
		/>
	</div>
	<div class="button-container">
		<Button
			text="REQUEST A FREEDOM WRITER"
			link="mailto:rick@freedomwritersfoundation.org"
		/>
	</div>
	<div class="video-container">
		<Video
			videoSRC="https://www.youtube.com/embed/J1WkvntQedQ?si=BtrwNNMmpxk0m23F"
		/>
	</div>
</section>
</main>

<style>
.below-text {
	margin: 1rem 7% 0 7%;
}
.image-text-container {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    margin: 0 7% 0 7%;
}

.text-wrapper {
    flex: 1; /* Takes as much width as available */
    min-width: 50%; /* Ensures the text takes at least 50% of the container width */
}
p {
    font-size: 1rem;
    font-family: 'Arial';
    line-height: 1.5;
}
.image-container {
	margin: 1rem;
    display: flex;
    flex-wrap: wrap;
    flex: 1;
    gap: 34px;
    justify-content: center;
}
.button-container {
	margin: 25px 0 25px 0;
	text-align: center;
}
.video-container {
	margin: 0 7% 25px 7%;
}
</style>